import React, {useCallback, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../../core/services/api/api";
import IconButton from "../../../../../../../base/button/icon_button";
import {Add, ArrowBack, Remove} from "@mui/icons-material";
import Button from "../../../../../../../base/button/button";
import {Col, Row} from "reactstrap";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnHighlightingMechanisms, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import {EditPartSupportRequestInvoicesDataGirdColumnNames} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import {DefaultPaginationInfo} from "../../../../../../../../../core/constants/enums";
import useSearch from "../../../../../../../../hooks/use-search";
import Form from "../../../../../../../base/form";
import {OutlinedInputForm} from "../../../../../../../base/input/outlined_input";
import {MuiRFF, Yup} from '../../../../../../../form';
import DateUtils from "../../../../../../../../../core/services/date-utils";
import ValidateMessages from "../../../../../../../../../core/models/static/texts/vallidate-messages";
import Utils from "../../../../../../../../../core/services/utils/utils";
import ProductInformationPopover from "../../../../../../../app-specific/draggable-popover/product-information-popover";
import {EditPartSupportRequestProcessSteps} from "../../../../index";

const EditPartSupportRequestProductInfoSectionInvoicesSection = ({
                                                                     productInfo,
                                                                     setProductInfo,
                                                                     customer,
                                                                     setProcess
                                                                 }) => {
    const [loading, setLoading] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const isMounted = useIsMounted();

    const isSerialized = useMemo(() => !!productInfo?.isSerialized, [productInfo?.isSerialized]);
    const inQuantitySelection = useMemo(() => !!productInfo?.invoiceNo && !productInfo?.serialNumber,
        [productInfo?.invoiceNo, productInfo?.serialNumber]);
    const canProceedToReview = useMemo(() => !isSerialized && productInfo?.selectedQuantity > 0,
        [isSerialized, productInfo?.selectedQuantity])

    const dataGridSaveConfig = useMemo(() =>
            inQuantitySelection
                ? ({
                    name: 'EditPartSupportRequestProductInfoSectionInvoicesSectionInQuantitySelectionDataGrid',
                    version: 1,
                })
                : !isSerialized
                    ? ({
                        name: 'EditPartSupportRequestProductInfoSectionInvoicesSectionUnSerializedDataGrid',
                        version: 1,
                    })
                    : ({
                        name: 'EditPartSupportRequestProductInfoSectionInvoicesSectionSerializedDataGrid',
                        version: 1,
                    })
        , [inQuantitySelection, isSerialized])

    /**
     * Determines the table columns to be shown in the table.
     *
     * * if inQuantitySelection, then returns the columns for available quantity table
     * * if not serializable, then returns the columns for un-serialized products
     * * otherwise returns the columns for serialized products
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() => {
        if (inQuantitySelection) {
            return [
                {
                    title: 'Invoice No',
                    name: EditPartSupportRequestInvoicesDataGirdColumnNames.invoiceNo,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    highlightingMechanism: DataGridColumnHighlightingMechanisms.diff,
                },
                {
                    title: 'Available Qty',
                    name: EditPartSupportRequestInvoicesDataGirdColumnNames.orderQty,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: 'Selected Qty',
                    name: EditPartSupportRequestInvoicesDataGirdColumnNames.selectedQty,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 150,
                },
            ]
        }
        if (!isSerialized) {
            return [
                {
                    title: 'Invoice No',
                    name: EditPartSupportRequestInvoicesDataGirdColumnNames.invoiceNo,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    highlightingMechanism: DataGridColumnHighlightingMechanisms.diff,
                },
                {
                    title: 'Date',
                    name: EditPartSupportRequestInvoicesDataGirdColumnNames.date,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.date,
                },
                {
                    title: 'Price',
                    name: EditPartSupportRequestInvoicesDataGirdColumnNames.price,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.money,
                },
                {
                    title: 'Order Count',
                    name: EditPartSupportRequestInvoicesDataGirdColumnNames.orderQty,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.string,
                },
                {
                    name: EditPartSupportRequestInvoicesDataGirdColumnNames.select,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    order: 5,
                    pinned: true,
                    pinnedType: DataGridColumnPinnedTypes.right,
                },
            ]
        }
        return [
            {
                title: 'Serial No',
                name: EditPartSupportRequestInvoicesDataGirdColumnNames.serialNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                highlightingMechanism: DataGridColumnHighlightingMechanisms.diff,
                sortable: false,
            },
            {
                title: 'Invoice No',
                name: EditPartSupportRequestInvoicesDataGirdColumnNames.invoiceNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                highlightingMechanism: DataGridColumnHighlightingMechanisms.diff,
            },
            {
                title: 'Date',
                name: EditPartSupportRequestInvoicesDataGirdColumnNames.date,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.date,
            },
            {
                title: 'Price',
                name: EditPartSupportRequestInvoicesDataGirdColumnNames.price,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
            },
            {
                name: EditPartSupportRequestInvoicesDataGirdColumnNames.select,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                order: 5,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
            },
        ]
    }, [inQuantitySelection, isSerialized])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Gets the list of drivers by calling the api regarding this action
     * If the function is being called for export, the pagination info is passed as the input
     * @type {(function(UseSearchFunctionArg): Promise<void>)}
     */
    const fetchEntries = useCallback(async (args) => {
        setLoading(true);
        const sorts = [];
        if (!!args.orderBy?.field) {
            sorts.push({
                isDesc: args.orderBy.descending,
                Property: args.orderBy.field,
            })
        }
        const forApi = {
            customerId: customer?.id,
            keyword: args.filters?.keyword ?? "",
            partNo: productInfo.part?.partNumber ?? "",
            sorts: sorts,
            period: {
                startDateTime: args.filters?.startDate ?? null,
                endDateTime: args.filters?.endDate ?? null,
            },
            pagination: args.paginationInfo,
        }
        const response = await Api.searchInvoicesBasedOnPart(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag) {
            const res = [];
            const isSerialized = response?.data?.items?.[0]?.serialNumbers !== null;
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0,
            }))
            if (isSerialized) {
                response.data?.items?.forEach(invoice => {
                    (invoice.serialNumbers ?? []).forEach(serial => {
                        res.push({...invoice, serialNo: serial});
                    })
                })
            } else {
                response.data?.items?.forEach(invoice => {
                    res.push(invoice);
                });
            }
            setProductInfo(prevState => ({
                ...prevState,
                isSerialized: isSerialized,
            }));
            setInvoices(res);
        }
    }, [isMounted, productInfo.part.partNumber, setProductInfo, customer?.id])

    /**
     * Searches among the entries based on the filters from the operator.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize,
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {
            keyword: '',
        },
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings, false);

    /**
     * Based on the serialize-ability of the selected part in the previous step:
     * either saves the invoice information and navigates to the next step or shows the quantity table for the
     * selected invoice.
     * @param invoice
     */
    const onInvoiceSelected = useCallback((invoice) => {
        if (isSerialized) {
            setProductInfo(prevState => ({
                ...prevState,
                selectedQuantity: null,
                serialNumber: invoice.serialNo,
                invoiceNo: invoice.invoiceNo,
                warehouseCode: null,
            }))
            return setProcess(EditPartSupportRequestProcessSteps.review);
        }
        setProductInfo(prevState => ({
            ...prevState,
            selectedQuantity: 0,
            serialNumber: null,
            invoiceNo: invoice.invoiceNo,
            warehouseCode: null,
        }))
    }, [isSerialized, setProcess, setProductInfo])

    /**
     * Changes the selected quantity of the product info for the selected un-serialized input.
     * @param newValue {number}
     * @param invoice {any}
     */
    const changeSelectedQuantity = useCallback((newValue, invoice) => {
        setProductInfo(prevState => ({
            ...prevState,
            selectedQuantity: !newValue
                ? null
                : Math.max(
                    0,
                    Math.min(
                        parseFloat(invoice.orderQty ?? '0'),
                        newValue
                    )
                )
        }));
    }, [setProductInfo])

    /**
     * Based on the current step of the process, determines what would be the appropriate back step.
     *
     * - if in selection of a quantity for an invoice no, goes back to invoices-view
     * - if in invoices, goes back to part selection
     */
    const goBack = () => {
        if (inQuantitySelection) {
            return setProductInfo(prevState => ({
                ...prevState,
                selectedQuantity: 0,
                serialNo: null,
                invoiceNo: null,
                warehouseCode: null,
            }))
        }
        setProductInfo(prevState => ({
            ...prevState,
            showProductOrSerialNoSelection: false,
            selectedQuantity: null,
            serialNo: null,
            invoiceNo: null,
            warehouseCode: null,
        }))
    }

    /**
     * Submits the selected quantity for the request and navigates the operator to review their changes
     */
    const submitQuantityChange = () => {
        setProcess(EditPartSupportRequestProcessSteps.review);
    }

    /**
     * Creates the data entries for the table
     * @type {DataGridRow[]}
     */
    const dataGridRows = useMemo(() => {
        return invoices
            ?.filter(e => inQuantitySelection ? e.invoiceNo === productInfo.invoiceNo : e)
            ?.map((invoice) => {
                return ({
                    key: `${invoice?.invoiceNo}-${invoice?.invoiceDate}-${invoice?.serialNo ?? Utils.createUUID()}`,
                    data: invoice,
                    cells: {
                        [EditPartSupportRequestInvoicesDataGirdColumnNames.invoiceNo]: invoice.invoiceNo,
                        [EditPartSupportRequestInvoicesDataGirdColumnNames.serialNo]: invoice.serialNo,
                        [EditPartSupportRequestInvoicesDataGirdColumnNames.date]: invoice.invoiceDate,
                        [EditPartSupportRequestInvoicesDataGirdColumnNames.price]: invoice.unitPrice,
                        [EditPartSupportRequestInvoicesDataGirdColumnNames.orderQty]: invoice.orderQty,
                        [EditPartSupportRequestInvoicesDataGirdColumnNames.selectedQty]:
                            <div className='d-flex w-100 align-items-center justify-content-center'>
                                <IconButton
                                    className={''}

                                    onPressed={() => changeSelectedQuantity((productInfo.selectedQuantity ?? 0) - 1, invoice)}
                                    color={'secondary'}
                                >
                                    <Remove className={'icon small'}/>
                                </IconButton>
                                <p {...dataGridCellTextProps} className={'text-md mx-4 secondary-color-1 font-weight-semi-bold'}>
                                    {productInfo.selectedQuantity ?? 0}
                                </p>
                                <IconButton

                                    onPressed={() => changeSelectedQuantity((productInfo.selectedQuantity ?? 0) + 1, invoice)}
                                    color={'secondary'}
                                >
                                    <Add className={'icon small'}/>
                                </IconButton>
                            </div>,
                        [EditPartSupportRequestInvoicesDataGirdColumnNames.select]:
                            <Button
                                className={'px-4 py-1'}
                                variant={'outlined'}
                                color={'secondary'}
                                onClick={() => onInvoiceSelected(invoice)}
                            >
                                Select
                            </Button>,
                    },
                })
            })
    }, [invoices, inQuantitySelection, productInfo.invoiceNo, productInfo.selectedQuantity, changeSelectedQuantity, onInvoiceSelected])

    return (
        <>
            <Col xs={12} className={'row no-gutters align-items-start justify-content-start mb-4'}>
                <IconButton
                    color={'secondary'}
                    onPressed={goBack}
                    style={{marginTop: 2}}
                >
                    <ArrowBack className={'icon normal'}/>
                </IconButton>
                <div className={'d-flex flex-column'}>
                    {
                        !inQuantitySelection &&
                        <ProductInformationPopover
                            partNo={productInfo?.part?.partNumber}
                            wrapperProps={{className: 'ml-3'}}
                            buttonProps={{className: ' pl-0'}}
                        >
                            <span className={'text-md'}>
                            {productInfo?.part?.partNumber ?? '--'}
                            </span>
                        </ProductInformationPopover>
                    }
                    {
                        inQuantitySelection &&
                        <p className={'text-md font-weight-semi-bold text-color-1 ml-3 part-number-fluid'}>
                            how many <ProductInformationPopover partNo={productInfo?.part?.partNumber}
                                                                wrapperProps={{className: 'd-inline-block'}}>
                            {productInfo?.part?.partNumber ?? '--'}
                        </ProductInformationPopover> would you like to apply for?
                        </p>
                    }
                    {
                        !inQuantitySelection &&
                        <p className={'text-sm grey-3 ml-3'}>
                            Back to lookup
                        </p>
                    }
                </div>
            </Col>
            <Col xs={12}>
                <SearchSection searchData={searchData}/>
            </Col>
            <Col xs={12} className={'mb-4'}>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    classNames={{
                        container: 'part-support-invoices-data-grid'
                    }}
                    rows={dataGridRows}
                    columns={dataGridColumns}
                    hideExporting
                    hideFooterActions
                    visibleRows={5}
                    state={{
                        loading: {state: loading},
                        density: dataGridDensity,
                        pagination: searchData.paginationInfo,
                    }}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </Col>
            {
                inQuantitySelection &&
                <Row className='no-gutters footer justify-content-between align-items-start w-100'>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={goBack}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Back
                    </Button>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        disabled={!canProceedToReview}
                        onClick={submitQuantityChange}
                        borderRadius={'8px 0 8px 0'}
                    >
                        Continue
                    </Button>
                </Row>
            }
        </>
    );
}


const schema = Yup.object().shape({
    keyword: Yup.string().nullable(),
    startDate: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
        .maxDate(Yup.ref('endDate'), ValidateMessages.lessThan('End Date'))
        .nullable(),
    endDate: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
        .minDate(Yup.ref('startDate'), ValidateMessages.greaterThan('Start Date'))
        .nullable(),
})

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const SearchSection = ({searchData}) => {

    /**
     * Submits the form and sets the filters of the search data.
     * @param values {any} form values
     */
    const onSubmit = useCallback((values) => {
        searchData.setFilters({
            ...values,
            startDate: values.startDate ? DateUtils.UTCToServer(values.startDate).format(DateUtils.withoutOffsetFormat) : undefined,
            endDate: values.endDate ? DateUtils.UTCToServer(values.endDate).format(DateUtils.withoutOffsetFormat) : undefined,
        });
    }, [searchData])

    const initialValues = useMemo(() => ({
        ...(searchData.filters ?? {}),
        startDate: searchData.filters?.startDate ? DateUtils.serverToUTC(searchData.filters.startDate) : undefined,
        endDate: searchData.filters?.endDate ? DateUtils.serverToUTC(searchData.filters.endDate) : undefined,
    }), [searchData.filters])

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            className={'d-flex w-100 flex-wrap px-3'}
            initialValues={initialValues}
            render={({values, form}) => {
                const colClassName = 'px-2 mb-4';
                return (
                    <>
                        <Col xs={12} className={colClassName}>
                            <OutlinedInputForm
                                onChange={(e) => form.change(e.target.name, e.target.value)}
                                value={values?.keyword ?? ''}
                                label={'Keywords'}
                                name={'keyword'}
                                type={'text'}
                                required={required.keyword}
                            />
                        </Col>
                        <Col xs={6} className={colClassName}>
                            <MuiRFF.DatePicker
                                name={'startDate'}
                                label={"Start Date"}
                                required={required.startDate}
                                disableFuture
                            />
                        </Col>
                        <Col xs={6} className={colClassName}>
                            <MuiRFF.DatePicker
                                name={'endDate'}
                                label={"End Date"}
                                required={required.endDate}
                                minDate={values.startDate}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12}>
                            <Button
                                disabled={searchData.loading}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className='d-flex mx-auto px-5 py-2-5'
                            >
                                {
                                    searchData.loading
                                        ? "Searching..."
                                        : "Search"
                                }
                            </Button>
                        </Col>
                    </>
                );
            }}
        />
    );
}

export default EditPartSupportRequestProductInfoSectionInvoicesSection;
